import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useMarkToolbarButton, useMarkToolbarButtonState } from '@platejs/utils/react';
import { toggleList, someList } from '@platejs/list';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import styles from './FloatingToolbar.module.scss';
import { FontSizeMenu } from '../FontSizeMenu/FontSizeMenu';
import { TextColorMenu } from '../TextColorMenu/TextColorMenu';
import { CellBackgroundMenu } from '../CellBackgroundMenu/CellBackgroundMenu';
import { AIToolbarButton } from '../AIToolbarButton/AIToolbarButton';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';

// Compact toolbar that appears just above any non-empty text selection
// inside the editor. Light theme so reused menus (font size, colors)
// match the main PlateToolbar look. Selection tracking via
// `selectionchange` + containment check against
// `[data-testid="plate-editor-content"]`. Rendered into <body> via portal
// so it can escape any overflow clipping on the editor wrapper.

interface MarkButtonProps {
    nodeType: string;
    label: string;
    children: React.ReactNode;
}

function MarkButton({ nodeType, label, children }: MarkButtonProps) {
    const state = (useMarkToolbarButtonState as any)({ nodeType });
    const { props: btnProps } = (useMarkToolbarButton as any)(state);
    return (
        <button
            type="button"
            {...btnProps}
            className={clsx(styles.btn, state.pressed && styles.btnActive)}
            title={label}
            aria-label={label}
            aria-pressed={state.pressed || undefined}
        >
            {children}
        </button>
    );
}

interface ListButtonProps {
    listStyleType: string;
    active: boolean;
    label: string;
    children: React.ReactNode;
}

function ListButton({ listStyleType, active, label, children }: ListButtonProps) {
    const editor = useEditorRef();
    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            (toggleList as any)(editor, { listStyleType });
        },
        [editor, listStyleType],
    );
    return (
        <button
            type="button"
            className={clsx(styles.btn, active && styles.btnActive)}
            onMouseDown={onMouseDown}
            title={label}
            aria-label={label}
            aria-pressed={active || undefined}
        >
            {children}
        </button>
    );
}

const BULLETED_STYLES = ['disc', 'circle', 'square'];
const NUMBERED_STYLES = ['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'];

export function FloatingToolbar() {
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

    const isBulletedList = useEditorSelector((ed: any) => (someList as any)(ed, BULLETED_STYLES), []);
    const isNumberedList = useEditorSelector((ed: any) => (someList as any)(ed, NUMBERED_STYLES), []);

    // Gate the AI group + its divider on `editor.AI.enabled`. Same belt-and-
    // suspenders pattern as PlateToolbar — the button null-returns when off,
    // but gating here avoids the empty `.group` div + dangling divider on
    // the left edge of the floating toolbar.
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });

    useEffect(() => {
        let raf = 0;
        // Cheap pre-check that short-circuits before scheduling a RAF on
        // every keystroke. `selectionchange` fires while the caret moves
        // during typing — the toolbar only ever shows on a non-collapsed
        // selection, so we can bail synchronously when collapsed.
        const isCollapsedNow = () => {
            const sel = document.getSelection();
            return !sel || sel.rangeCount === 0 || sel.isCollapsed;
        };
        const update = () => {
            if (isCollapsedNow()) {
                cancelAnimationFrame(raf);
                setPos(null);
                return;
            }
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const editorEl = document.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null;
                if (!editorEl) {
                    setPos(null);
                    return;
                }
                // Keep the toolbar visible while a popover (font size,
                // color pickers) is open — those mount as DOM children of
                // the toolbar, so `activeElement` may move into them.
                const active = document.activeElement;
                const toolbarEl = document.querySelector('[data-testid="plate-floating-toolbar"]');
                const focusInsideEditor = active && editorEl.contains(active);
                const focusInsideToolbar = active && toolbarEl && toolbarEl.contains(active);
                if (!focusInsideEditor && !focusInsideToolbar) {
                    setPos(null);
                    return;
                }
                const sel = document.getSelection();
                if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
                    setPos(null);
                    return;
                }
                const range = sel.getRangeAt(0);
                if (!editorEl.contains(range.commonAncestorContainer)) {
                    setPos(null);
                    return;
                }
                const rect = range.getBoundingClientRect();
                if (!rect || rect.width < 2 || rect.height < 2) {
                    setPos(null);
                    return;
                }
                setPos({ left: rect.left + rect.width / 2, top: rect.top });
            });
        };
        // Name every listener so removeEventListener gets the same
        // reference. Anonymous arrows leak — every editor mount used to
        // accumulate stale listeners on document/window.
        const hide = () => setPos(null);
        const onFocusOut = () => requestAnimationFrame(update);
        document.addEventListener('selectionchange', update);
        document.addEventListener('focusin', update);
        document.addEventListener('focusout', onFocusOut);
        window.addEventListener('blur', hide);
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('selectionchange', update);
            document.removeEventListener('focusin', update);
            document.removeEventListener('focusout', onFocusOut);
            window.removeEventListener('blur', hide);
            // The capture flag MUST match — addEventListener(_, _, true) and
            // removeEventListener(_, _) target different listener slots and
            // the latter is a silent no-op.
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, []);

    if (!pos || typeof document === 'undefined') return null;
    return createPortal(
        <div
            role="toolbar"
            aria-label="Inline formatting"
            data-testid="plate-floating-toolbar"
            className={styles.floating}
            style={{ left: pos.left, top: pos.top }}
            // Stop mousedown from collapsing the selection (the toolbar buttons
            // need the selection alive to apply marks to it).
            onMouseDown={(e) => e.preventDefault()}
        >
            {/* AI assistant — leftmost slot, only renders when `editor.AI.enabled`
                is on. Passing FloatingToolbar's own `btn` class so the button
                matches the host toolbar's chrome instead of inheriting
                PlateToolbar's. */}
            {aiEnabled.write && (
                <>
                    <div className={styles.group}>
                        <AIToolbarButton className={styles.btn} />
                    </div>
                    <div className={styles.divider} aria-hidden />
                </>
            )}

            <div className={styles.group}>
                <FontSizeMenu />
            </div>
            <div className={styles.divider} aria-hidden />

            <div className={styles.group}>
                <MarkButton nodeType="bold" label="Bold (⌘/Ctrl+B)">
                    <FormatBoldIcon fontSize="small" />
                </MarkButton>
                <MarkButton nodeType="italic" label="Italic (⌘/Ctrl+I)">
                    <FormatItalicIcon fontSize="small" />
                </MarkButton>
                <MarkButton nodeType="underline" label="Underline (⌘/Ctrl+U)">
                    <FormatUnderlinedIcon fontSize="small" />
                </MarkButton>
                <MarkButton nodeType="strikethrough" label="Strikethrough">
                    <FormatStrikethroughIcon fontSize="small" />
                </MarkButton>
            </div>
            <div className={styles.divider} aria-hidden />

            <div className={styles.group}>
                <TextColorMenu />
                {/* Self-disables when the selection isn't inside a <td>/<th>;
                    no outer gating needed. Mirrors PlateToolbar's ordering
                    (text color → cell fill). */}
                <CellBackgroundMenu />
            </div>
            <div className={styles.divider} aria-hidden />

            <div className={styles.group}>
                <ListButton listStyleType="disc" active={isBulletedList} label="Bulleted list">
                    <FormatListBulletedIcon fontSize="small" />
                </ListButton>
                <ListButton listStyleType="decimal" active={isNumberedList} label="Numbered list">
                    <FormatListNumberedIcon fontSize="small" />
                </ListButton>
            </div>
        </div>,
        document.body,
    );
}
